import type {
  BrowserChromeApi,
  BrowserCreateTabOptions,
  BrowserEditorTabCatalog,
  BrowserHandle,
  BrowserOpenOptions,
  BrowserState,
  BrowserTabState,
} from '@hibro/shared';
import {
  browserEditorTabTitle,
  decideBrowserNavigation,
  normalizeBrowserUrl,
  parkedBrowserEditorTabTitle,
  syncBrowserEditorTab,
} from '@hibro/shared';

const WEB_BROWSER_HOST_ID = 'browser-web-editor';
const WEB_BROWSER_CAPABILITIES = {
  screenshot: false,
  hardReload: false,
  pageZoom: false,
  findInPage: false,
  downloads: false,
  clearCookies: false,
  sitePermissions: false,
  completeNavigationEvents: false,
} as const;

interface WebBrowserTab extends BrowserTabState {
  documentVersion: number;
}

/** Pull the URL a detached editor tab was opened with (见 22). */
export function detachedEditorUrlFromPayload(
  payload: unknown,
): string | undefined {
  if (
    payload &&
    typeof payload === 'object' &&
    'url' in payload &&
    typeof payload.url === 'string'
  ) {
    return payload.url;
  }
  return undefined;
}

export interface WebBrowserHost {
  readonly chromeApi: BrowserChromeApi;
  /** Resolves the editor slot id so the workbench can open that instance tab. */
  open(options: BrowserOpenOptions): Promise<string>;
  /**
   * Recreate one editor slot in a detached document (见 22). A missing or
   * illegal URL becomes an error tab rather than a blank page.
   */
  seedEditorSlot(slotId: string, url: string | undefined): void;
  dispose(): void;
}

export function createWebBrowserHost(options: {
  openExternal: (url: string) => void;
  presentation?: 'editor' | 'window';
  initialUrl?: string;
  createTabId?: () => string;
  /**
   * Persists committed editor-tab URLs across reloads. Omitted only by tests
   * that are not exercising restoration. The workbench always passes one.
   */
  editorTabs?: BrowserEditorTabCatalog;
}): WebBrowserHost {
  const listeners = new Set<(state: BrowserState) => void>();
  const presentation = options.presentation ?? 'editor';
  let disposed = false;
  let tabs: WebBrowserTab[] = [];
  let activeTabId = '';
  let nextTabId = 0;

  const createTabId = (): string =>
    options.createTabId?.() ??
    globalThis.crypto?.randomUUID?.() ??
    `browser-web-tab-${++nextTabId}`;

  const tabById = (id: string | undefined): WebBrowserTab | undefined =>
    tabs.find((tab) => tab.id === id);

  const resolveTab = (slotId?: string): WebBrowserTab | undefined =>
    tabById(slotId) ?? tabById(activeTabId);

  const requireTab = (slotId?: string): WebBrowserTab => {
    const tab = resolveTab(slotId);
    if (!tab?.url) {
      throw new Error('Web built-in browser has no active page');
    }
    return tab;
  };

  const snapshot = (slotId?: string): BrowserState => {
    if (presentation === 'editor') {
      const active = resolveTab(slotId);
      const savedUrl = active
        ? undefined
        : options.editorTabs?.read(slotId ?? '');
      const savedTitle = active
        ? undefined
        : options.editorTabs?.readTitle(slotId ?? '');
      return {
        id: WEB_BROWSER_HOST_ID,
        presentation,
        slotId: active?.id ?? (slotId || undefined),
        activeTabId: active?.id ?? '',
        tabs: active ? [{ ...active }] : [],
        url: active?.url ?? savedUrl ?? '',
        title:
          active?.title ?? parkedBrowserEditorTabTitle(savedUrl, savedTitle),
        loading: active?.loading ?? false,
        canGoBack: false,
        canGoForward: false,
        capabilities: WEB_BROWSER_CAPABILITIES,
        navigationRevision: active?.navigationRevision ?? 0,
        documentVersion: active?.documentVersion ?? 0,
        ...(active?.error !== undefined ? { error: active.error } : {}),
      };
    }
    const active = tabById(activeTabId);
    return {
      id: 'browser-web-window',
      presentation,
      activeTabId,
      tabs: tabs.map((tab) => ({ ...tab })),
      url: active?.url ?? '',
      title: active?.title ?? 'HiBro Browser',
      loading: active?.loading ?? false,
      canGoBack: false,
      canGoForward: false,
      capabilities: WEB_BROWSER_CAPABILITIES,
      navigationRevision: active?.navigationRevision ?? 0,
      documentVersion: active?.documentVersion ?? 0,
      ...(active?.error !== undefined ? { error: active.error } : {}),
    };
  };

  const emit = (slotId?: string): void => {
    const state = snapshot(slotId);
    for (const listener of [...listeners]) {
      listener(state);
    }
  };

  const assertActive = (): void => {
    if (disposed) {
      throw new Error('Web built-in browser host is disposed');
    }
  };

  const recordEditorTab = (
    slotId: string,
    url: string,
    title?: string,
  ): void => {
    if (presentation !== 'editor' || !options.editorTabs) {
      return;
    }
    syncBrowserEditorTab(options.editorTabs, slotId, 'persistent', url, title);
  };

  const appendTab = (
    createOptions: BrowserCreateTabOptions = {},
    id = createTabId(),
  ): BrowserHandle => {
    assertActive();
    const url = createOptions.url ? normalizeBrowserUrl(createOptions.url) : '';
    const tab: WebBrowserTab = {
      id,
      url,
      title: url ? browserEditorTabTitle(url) : 'New Tab',
      loading: false,
      canGoBack: false,
      canGoForward: false,
      documentVersion: url ? 1 : 0,
      navigationRevision: url ? 1 : 0,
    };
    tabs = [...tabs, tab];
    activeTabId = id;
    if (url) {
      recordEditorTab(id, url, tab.title);
    }
    emit(id);
    return { id };
  };

  const navigate = (input: string, slotId?: string): void => {
    assertActive();
    const decision = decideBrowserNavigation(input, 'chrome');
    if (decision.action === 'open-external') {
      options.openExternal(decision.url);
      return;
    }
    if (decision.action === 'reject') {
      throw new Error(decision.reason);
    }
    const url = decision.url;
    const current = resolveTab(slotId);
    if (!current) {
      appendTab({ url }, slotId ?? createTabId());
      return;
    }
    tabs = tabs.map((tab) =>
      tab.id === current.id
        ? {
            ...tab,
            url,
            title: browserEditorTabTitle(url),
            loading: false,
            documentVersion: tab.documentVersion + 1,
            navigationRevision: (tab.navigationRevision ?? 0) + 1,
            error: undefined,
          }
        : tab,
    );
    activeTabId = current.id;
    recordEditorTab(current.id, url, browserEditorTabTitle(url));
    emit(current.id);
  };

  const unsupported = (operation: string): Promise<never> =>
    Promise.reject(
      new Error(`Web built-in browser does not support ${operation}`),
    );

  const dropEditorSlot = (slotId: string): void => {
    tabs = tabs.filter((tab) => tab.id !== slotId);
    if (activeTabId === slotId) {
      activeTabId = tabs[tabs.length - 1]?.id ?? '';
    }
  };

  const chromeApi: BrowserChromeApi = {
    getState: (slotId) => Promise.resolve(snapshot(slotId)),
    attachEditorSlot: (slotId) => {
      if (presentation !== 'editor') {
        return unsupported('editor slot attachment in window presentation');
      }
      if (!tabById(slotId)) {
        const saved = options.editorTabs?.read(slotId);
        if (saved) {
          navigate(saved, slotId);
        } else {
          appendTab({}, slotId);
        }
      }
      return Promise.resolve(snapshot(slotId));
    },
    navigate: (url, slotId) => {
      try {
        navigate(url, slotId);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    },
    goBack: () => unsupported('back navigation'),
    goForward: () => unsupported('forward navigation'),
    reload: (slotId) => {
      const current = requireTab(slotId);
      tabs = tabs.map((tab) =>
        tab.id === current.id
          ? { ...tab, documentVersion: tab.documentVersion + 1 }
          : tab,
      );
      emit(current.id);
      return Promise.resolve();
    },
    openExternal: (slotId) => {
      options.openExternal(requireTab(slotId).url);
      return Promise.resolve();
    },
    openDevTools: () => unsupported('developer tools'),
    createTab: (createOptions) => {
      if (presentation !== 'window') {
        return unsupported('tab creation in editor presentation');
      }
      if (createOptions?.partition === 'ephemeral') {
        return unsupported('ephemeral storage partitions');
      }
      return Promise.resolve(appendTab(createOptions));
    },
    selectTab: (tabId) => {
      if (presentation !== 'window') {
        return unsupported('tab selection in editor presentation');
      }
      if (!tabs.some((tab) => tab.id === tabId)) {
        return Promise.reject(new Error(`Unknown browser tab: ${tabId}`));
      }
      activeTabId = tabId;
      emit();
      return Promise.resolve();
    },
    closeTab: (tabId) => {
      if (presentation !== 'window') {
        return unsupported('tab closing in editor presentation');
      }
      const index = tabs.findIndex((tab) => tab.id === tabId);
      if (index < 0) {
        return Promise.reject(new Error(`Unknown browser tab: ${tabId}`));
      }
      tabs = tabs.filter((tab) => tab.id !== tabId);
      if (activeTabId === tabId) {
        activeTabId = tabs[Math.min(index, tabs.length - 1)]?.id ?? '';
      }
      emit();
      return Promise.resolve();
    },
    disposeEditorSlot: (slotId) => {
      if (presentation !== 'editor') {
        return unsupported('editor slot disposal in window presentation');
      }
      dropEditorSlot(slotId);
      options.editorTabs?.forget(slotId);
      emit(slotId);
      return Promise.resolve();
    },
    suspendEditorSlot: (slotId) => {
      if (presentation !== 'editor') {
        return unsupported('editor slot suspension in window presentation');
      }
      dropEditorSlot(slotId);
      emit(slotId);
      return Promise.resolve();
    },
    onState: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };

  const seedFailedSlot = (slotId: string, message: string): void => {
    const tab: WebBrowserTab = {
      id: slotId,
      url: '',
      title: 'HiBro Browser',
      loading: false,
      canGoBack: false,
      canGoForward: false,
      documentVersion: 0,
      error: message,
    };
    tabs = tabById(slotId)
      ? tabs.map((current) => (current.id === slotId ? tab : current))
      : [...tabs, tab];
    activeTabId = slotId;
    emit(slotId);
  };

  const host: WebBrowserHost = {
    chromeApi,
    seedEditorSlot(slotId, url) {
      if (!url) {
        seedFailedSlot(slotId, 'Detached browser editor is missing its URL');
        return;
      }
      try {
        navigate(url, slotId);
      } catch (error) {
        seedFailedSlot(
          slotId,
          error instanceof Error ? error.message : String(error),
        );
      }
    },
    open(browserOptions) {
      if (browserOptions.presentation === 'window') {
        navigate(browserOptions.url);
        const message =
          'Independent Web browser windows require a user gesture; use the window button in the browser editor';
        return Promise.reject(new Error(message));
      }
      if (browserOptions.partition === 'ephemeral') {
        return unsupported('ephemeral storage partitions');
      }
      const url = normalizeBrowserUrl(browserOptions.url);
      const reuse = browserOptions.reuse !== false;
      if (reuse) {
        const existing = tabs.find((tab) => tab.url === url);
        if (existing) {
          activeTabId = existing.id;
          emit(existing.id);
          return Promise.resolve(existing.id);
        }
      }
      return Promise.resolve(appendTab({ url }).id);
    },
    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      listeners.clear();
    },
  };

  if (options.initialUrl) {
    navigate(options.initialUrl);
  }

  return host;
}
